/**
 * ОДНА ДВЕРЬ, ЧЕРЕЗ КОТОРУЮ ЗНАЧЕНИЕ С СЕРВЕРА ЗАГЛЯДЫВАЕТ В КАРТУ ФОЛБЭКОВ.
 *
 * Правило незнакомого значения записано в `entities/booking/model/server-drift.ts`: «Незнакомое
 * значение НЕ РОНЯЕТ строку и НЕ УТВЕРЖДАЕТ ЛИШНЕГО — оно уезжает в САМЫЙ КОНСЕРВАТИВНЫЙ фолбэк,
 * тот, который НИЧЕГО НЕ ОБЕЩАЕТ». Здесь его ИСПОЛНЕНИЕ, и оно ровно одно на весь фронт.
 *
 * ⚠️ ЧТО БЫЛО СЛОМАНО: `Record<string, X>` + `?? фолбэк` МОЛЧА НАРУШАЕТ ЭТОТ ИНВАРИАНТ. Объектный
 * литерал наследует `Object.prototype` с его `toString`, `valueOf`, `constructor`, `hasOwnProperty`,
 * `__proto__` и прочими; придёт с сервера ключ с таким именем — и `MAP[key]` вернёт ФУНКЦИЮ ИЗ
 * ПРОТОТИПА. Функция истинна, значит `?? ФОЛБЭК` не срабатывает, и вся защита разом не наступает:
 *
 *     MAP['payout']    // → undefined            ✓ фолбэк работает
 *     MAP['toString']  // → [Function toString]  ✗ фолбэк НЕ работает, наружу уехала функция
 *
 * Дальше как повезёт: у карты, отдающей CSS-токен, худший исход — кривая строка в `--tint`. Но
 * стоит такой карте оказаться перед вызовом, который БРОСАЕТ, — и незнакомый ключ уносит приложение
 * на экран смерти. Так и случилось: `detailRoadmapKey` вернул `Object.prototype.toString`,
 * `RoadmapBadge` сделал `roadmapEntry(fn).badge`, `ROADMAP[fn]` дал `undefined`, `.badge` бросил
 * `TypeError` — и `RootErrorBoundary` унёс ВЕСЬ экран, а не одну строку деталей.
 *
 * ⚠️ Самой карты `detailRoadmapKey` больше нет (снята 29.08 вместе со строкой «Тариф», надгробие —
 * в `entities/booking/model/details.ts`); случай оставлен как ИСТОРИЯ, которой правило оплачено.
 * Класс никуда не делся: дверь нужна всем картам, чьи ключи задаёт сервер.
 *
 * ⚠️ Дыра МОЛЧАЩАЯ: `tsc` типизирует `MAP[key]` как `X | undefined` (`noUncheckedIndexedAccess`) и
 * этим ВРЁТ — на рантайме там бывает функция.
 *
 * ⚠️ ПОЧЕМУ `Map`, А НЕ `Object.hasOwn` И НЕ `Object.create(null)`. Все три чинят рантайм одинаково,
 * но первые два чинят только те карты, где о них не забыли: соседнее `MAP[key] ?? ФОЛБЭК`
 * компилируется как ни в чём не бывало, и дыра возвращается тем же способом. Правило, живущее только
 * в комментарии, машина не сторожит.
 *
 * У `ReadonlyMap` НЕТ ИНДЕКСНОЙ СИГНАТУРЫ: `MAP[key]` не компилируется вовсе, а `MAP.get(key)`
 * физически не умеет вернуть член прототипа. Отсюда три следствия: обойти дверь нельзя; тип перестал
 * врать (`T | undefined` из `Map.get` — правда); фолбэк нельзя забыть — он третий ОБЯЗАТЕЛЬНЫЙ
 * аргумент `pick`, а не `??`, который дописывают по памяти. Цена — один `Object.entries` на карту
 * при загрузке модуля.
 *
 * ⚠️ ЧТО СЮДА НЕ ОТНОСИТСЯ: `ROADMAP` (`shared/config/roadmap.ts`). Её ключ — `RoadmapKey`, замкнутое
 * объединение НАШИХ СОБСТВЕННЫХ ключей, а не строка с провода; фолбэка у неё нет и быть не должно —
 * реестр обязан рухнуть сборкой, если ключ исчез. Безопасна она ровно до тех пор, пока каждый, кто
 * добывает `RoadmapKey` из серверных данных, честен насчёт своего типа; сегодня таких нет ни одного.
 */

/**
 * Карта, в которую МОЖНО заглянуть значением, пришедшим с сервера.
 *
 * Читается только через `pick` (или `.get`) — индексом не читается вовсе, и в этом её работа.
 */
export type ServerMap<T> = ReadonlyMap<string, T>

/**
 * Собрать карту фолбэков из обычного литерала.
 *
 * Литерал остаётся литералом — читаемым, и с `satisfies Record<Union, T>` на полноту, если карта
 * обязана покрывать всё перечисление контракта (так устроен `STATE_TOKENS` в `model/payment.ts`).
 * `Object.entries` берёт только СОБСТВЕННЫЕ перечислимые ключи, так что ничего из `Object.prototype`
 * в карту не попадает — а `Map` и не полез бы туда при чтении.
 */
export function serverMap<T>(entries: Record<string, T>): ServerMap<T> {
  return new Map(Object.entries(entries))
}

/**
 * Значение по ключу с сервера — или фолбэк, если такого ключа в карте нет.
 *
 * **Один аргумент — один результат.** Незнакомый ключ (`payout`), пустая строка, кириллица,
 * человеческая подпись вместо машинного ключа и любое имя из `Object.prototype` (`toString`,
 * `constructor`, `__proto__`) дают ОДНО И ТО ЖЕ: фолбэк. Ничего не бросается, ничего лишнего не
 * утверждается.
 *
 * Фолбэк — обязательный аргумент, а не `??` на стороне вызова: тот, кто заводит карту, обязан
 * назвать самый консервативный ответ ПРЯМО ЗДЕСЬ. `F` отдельным параметром типа, чтобы фолбэком
 * можно было расширить результат (`null` у `deliveryMark`; `undefined` был у снятого `detailRoadmapKey`).
 *
 * Отсутствие ключа — это `undefined` из `Map.get`, а не ложность значения: карта, хранящая пустую
 * строку или `0`, вернула бы их, а не фолбэк. Сегодня таких нет, но правило должно быть одно.
 */
export function pick<T, F>(map: ServerMap<T>, key: string, fallback: F): T | F {
  const value = map.get(key)
  return value === undefined ? fallback : value
}
